import { compact, map } from "lodash";
import { ExportType, Frequency } from "PFApp/analytics/types/export_analytics";
import { useExportFields } from "PFCore/hooks/queries/analytics/booking_export/use_export_fields";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAnalyticsModalContext } from "../analytics_modal_context_provider";
import { decamelizeFilters } from "./export_fields_utils";
import { convertFieldsResponse } from "./helpers";
import { ExportTab, FieldsTab, FiltersTab, TemplateAndReportTypeTab } from "./tabs";
import { useRequiredFields } from "./use_required_fields";

const STANDARD_FIELDS_PREFIX = "standardFields";

export type TabType = "templateAndReportType" | "fields" | "filters" | "export";

type Tab = {
  id: TabType;
  displayElement: React.ReactNode;
};

type UseExportWizardTabs = {
  currentTab: TabType;
  tabs: Tab[];
  handleClickTab: (tab: Tab) => void;
  TabComponent: React.FC;
};

const TAB_COMPONENTS = {
  templateAndReportType: TemplateAndReportTypeTab,
  fields: FieldsTab,
  filters: FiltersTab,
  export: ExportTab
};

export const useExportWizardTabs = (): UseExportWizardTabs => {
  const {
    state: { data },
    updateData
  } = useAnalyticsModalContext();
  const { currentTab = "templateAndReportType", selectedFields } = data || {};
  const { t } = useTranslation("insights", { keyPrefix: "exportWizard" });
  const { data: exportFields } = useExportFields();
  const { requiredKeys } = useRequiredFields();

  const tabs: Tab[] = useMemo(
    () => [
      { id: "templateAndReportType", displayElement: t("tabs.templateAndReportType") },
      { id: "fields", displayElement: t("tabs.fields") },
      {
        id: "filters",
        displayElement: t("tabs.filters"),
        disabled: !selectedFields?.length
      },
      {
        id: "export",
        displayElement: t("tabs.export"),
        disabled: !selectedFields?.length
      }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedFields]
  );

  const TabComponent = useMemo(() => TAB_COMPONENTS[currentTab], [currentTab]);

  const handleClickTab = (value: Tab) => updateData({ currentTab: value.id });

  const buildFieldId = (fieldKey: string) => `${STANDARD_FIELDS_PREFIX}-${fieldKey}`;

  useEffect(() => {
    const { selectedTemplate } = data;
    if (selectedTemplate) {
      updateData({
        exportType: ExportType.Template,
        selectedFields: convertFieldsResponse(selectedTemplate.fields),
        selectedFilters: decamelizeFilters(selectedTemplate.filters),
        frequency: (selectedTemplate.aggregateBy || Frequency.Daily) as Frequency
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const areAllMandatoryFieldsSelected = requiredKeys.every((fieldKey) =>
      selectedFields.some(({ id }) => id === buildFieldId(fieldKey))
    );

    if (!areAllMandatoryFieldsSelected) {
      const fieldsToUpdate = compact(
        requiredKeys.flatMap((key) =>
          map(exportFields?.entries[STANDARD_FIELDS_PREFIX], (item, fieldKey) => {
            if (fieldKey === key) {
              return {
                ...item,
                id: buildFieldId(fieldKey)
              };
            }
          })
        )
      );

      updateData({ selectedFields: [...selectedFields, ...fieldsToUpdate] });
    }
  }, [selectedFields, exportFields?.entries, requiredKeys, updateData]);

  return {
    currentTab,
    tabs,
    handleClickTab,
    TabComponent
  };
};
